/**
 * Storage Health Manager - Probes storage quota and manages storage health
 * Prevents QuotaExceededError by proactively monitoring and cleaning storage
 */

interface StorageQuota {
  usage: number;
  quota: number;
  usagePercentage: number;
}

export class StorageHealthManager {
  private static instance: StorageHealthManager;
  private isStorageHealthy = true;
  private lastProbeTime = 0;
  private probeInterval = 30000; // 30 seconds
  private quotaThreshold = 0.8; // Disable persistence when 80% full

  static getInstance(): StorageHealthManager {
    if (!StorageHealthManager.instance) {
      StorageHealthManager.instance = new StorageHealthManager();
    }
    return StorageHealthManager.instance;
  }

  /**
   * Probe storage quota using navigator.storage.estimate()
   * Returns storage health status
   */
  async probeStorageQuota(): Promise<StorageQuota | null> {
    try {
      if (!navigator.storage?.estimate) {
        console.warn('Storage API not available');
        return null;
      }

      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const usagePercentage = quota > 0 ? usage / quota : 0;

      this.lastProbeTime = Date.now();
      this.isStorageHealthy = usagePercentage < this.quotaThreshold;

      console.log(`📊 Storage Health: ${Math.round(usagePercentage * 100)}% used (${Math.round(usage / 1024 / 1024)}MB / ${Math.round(quota / 1024 / 1024)}MB)`);

      return {
        usage,
        quota,
        usagePercentage
      };
    } catch (error) {
      console.warn('Storage quota probe failed:', error);
      return null;
    }
  }

  /**
   * Check if storage operations should be allowed
   */
  async isStorageAvailable(): Promise<boolean> {
    // Re-probe if last check was too long ago
    if (Date.now() - this.lastProbeTime > this.probeInterval) {
      await this.probeStorageQuota();
    }

    return this.isStorageHealthy;
  }

  /**
   * Test if localStorage can accept writes
   */
  testLocalStorageWrite(): boolean {
    try {
      const testKey = '__storage_health_test__';
      const testValue = 'test';
      localStorage.setItem(testKey, testValue);
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.warn('🚨 localStorage quota exceeded');
        this.isStorageHealthy = false;
        return false;
      }
      return false;
    }
  }

  /**
   * Emergency cleanup of large localStorage items
   */
  async emergencyCleanup(): Promise<void> {
    const largeItemsToClean = [
      'adt-audit-trail',
      'audit-trail',
      'simulation-result',
      'cached-scenarios',
      'monte-carlo-cache',
      'chart-data-cache',
      'historical-results',
      'backup-scenarios',
      'temp-calculations'
    ];

    let cleanedSize = 0;
    for (const key of largeItemsToClean) {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const itemSize = item.length * 2; // Rough size estimate (UTF-16)
          if (itemSize > 100000) { // Items > 100KB
            localStorage.removeItem(key);
            cleanedSize += itemSize;
            console.log(`🧹 Cleaned ${key}: ${Math.round(itemSize / 1024)}KB`);
          }
        }
      } catch (error) {
        console.warn(`Failed to clean ${key}:`, error);
      }
    }

    if (cleanedSize > 0) {
      console.log(`✅ Emergency cleanup: ${Math.round(cleanedSize / 1024)}KB freed`);
      // Re-test storage after cleanup
      this.testLocalStorageWrite();
    }
  }

  /**
   * Get storage usage breakdown by key prefix
   */
  getStorageBreakdown(): { [prefix: string]: number } {
    const breakdown: { [prefix: string]: number } = {};
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          if (value) {
            const prefix = key.split('-')[0] || 'other';
            const size = value.length * 2; // UTF-16 estimate
            breakdown[prefix] = (breakdown[prefix] || 0) + size;
          }
        }
      }
    } catch (error) {
      console.warn('Failed to analyze storage breakdown:', error);
    }

    return breakdown;
  }

  /**
   * Check storage health on app boot
   */
  async initializeStorageHealth(): Promise<void> {
    console.log('🔍 Checking storage health on boot...');
    
    // Immediate cleanup of known problematic keys
    await this.emergencyCleanup();
    
    // Probe storage quota
    const quota = await this.probeStorageQuota();
    
    // Test localStorage write capability
    const canWrite = this.testLocalStorageWrite();
    
    if (!canWrite || (quota && quota.usagePercentage > this.quotaThreshold)) {
      console.warn('🚨 Storage unhealthy - switching to memory-only mode');
      this.isStorageHealthy = false;
    } else {
      console.log('✅ Storage healthy');
    }
  }
}

export const storageHealthManager = StorageHealthManager.getInstance();